import { defineStore } from "pinia";
import { ref } from "vue";
import { settingsService } from "@/services/settingsService";
import { SCOPE, CLIENT_CONFIG_SECTION, toBackendScope } from "@/constants/scope";

// Server-persisted per-user "Action Needed" settings (LAM-4234 / LAM-4269).
// - `statuses` are admin-configured (which statuses count as action needed);
//   null until loaded, so consumers fall back to their historical defaults.
// - `showChip` is the user's own chip-visibility toggle, persisted server-side
//   for the current login session; the backend resets it to on at every login (LAM-5340).
export const useActionNeededStore = defineStore("actionNeeded", () => {
  const statuses = ref({ [SCOPE.PA]: null, [SCOPE.INTAKE]: null });
  const showChip = ref({ [SCOPE.PA]: true, [SCOPE.INTAKE]: true });
  const options = ref({ [SCOPE.PA]: [], [SCOPE.INTAKE]: [] });
  const assignedCasesOnly = ref(null);
  const loaded = ref(false);
  let inFlight = null;
  let loadGen = 0;

  function _apply(data) {
    if (typeof data?.assignedCasesOnly === "boolean") assignedCasesOnly.value = data.assignedCasesOnly;
    for (const scope of [SCOPE.PA, SCOPE.INTAKE]) {
      const section = data?.[CLIENT_CONFIG_SECTION[scope]];
      if (!section) continue;
      statuses.value[scope] = Array.isArray(section.statuses) ? section.statuses : null;
      showChip.value[scope] = section.showChip !== false;
      options.value[scope] = section.options || [];
    }
    loaded.value = true;
  }

  async function ensureLoaded() {
    if (loaded.value) return;
    if (!inFlight) {
      // Tag each fetch so a stale one (superseded by reload()) can't apply its
      // result or clear a newer fetch's handle.
      const gen = ++loadGen;
      inFlight = settingsService
        .getMyActionNeeded()
        .then((data) => {
          if (gen === loadGen) _apply(data);
        })
        .catch(() => {
          // Degrade to the historical fallback; retry on the next ensureLoaded().
        })
        .finally(() => {
          if (gen === loadGen) inFlight = null;
        });
    }
    return inFlight;
  }

  async function reload() {
    loaded.value = false;
    inFlight = null;
    return ensureLoaded();
  }

  async function setShowChip(scope, val) {
    const previous = showChip.value[scope];
    showChip.value[scope] = val;
    try {
      await settingsService.updateMyActionNeededChip(toBackendScope(scope), val);
    } catch (error) {
      showChip.value[scope] = previous;
      throw error;
    }
  }

  return { statuses, showChip, options, assignedCasesOnly, loaded, ensureLoaded, reload, setShowChip };
});
